import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, Select, Textarea, Modal, EmptyState } from '../../components';
import * as sessionService from '../../services/group-session-service';
import { setSessionMeetLink } from '../../services/group-session-service';
import { getUsers } from '../../services/user-service';
import { getMentorSlots, MentorWithSlots } from '../../services/mentor-availability-service';

interface MentorOption { id: string; name: string; }
interface FounderOption { id: string; name: string; startupName: string; }

const DURATION_OPTIONS = [
  { value: '30',  label: '30 minutes' },
  { value: '60',  label: '60 minutes' },
  { value: '90',  label: '90 minutes' },
  { value: '120', label: '120 minutes' },
];

const STATUS_BADGE: Record<string, string> = {
  scheduled:   'bg-chart/15 text-chart',
  in_progress: 'bg-warning/15 text-warning',
  completed:   'bg-success/15 text-success',
  cancelled:   'bg-danger/15 text-danger',
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

/* ── Detail Modal ── */
const DetailModal: React.FC<{ session: any; onClose: () => void; onDelete: (id: string) => void; onLinkSaved: (id: string, link: string) => void }> = ({ session, onClose, onDelete, onLinkSaved }) => {
  const participants: any[] = session.participants ?? [];
  const attendees: any[]   = session.attendees ?? [];
  const mentorName = session.mentorId?.name || session.mentorName || 'TBA';
  const past = new Date(session.scheduledAt).getTime() + (session.duration || 60) * 60 * 1000 < Date.now();
  const [linkInput, setLinkInput] = useState('');
  const [savingLink, setSavingLink] = useState(false);

  const merged = participants.length
    ? participants.map((p: any) => {
        const att = attendees.find((a: any) => String(a.userId) === String(p.userId));
        return { name: p.name || 'Unknown', status: att?.status ?? 'invited' };
      })
    : attendees.map((a: any) => ({ name: a.name || 'Unknown', status: a.status }));

  const statusColor: Record<string, string> = {
    present:   'bg-success/15 text-success',
    absent:    'bg-danger/15 text-danger',
    confirmed: 'bg-chart/15 text-chart',
    invited:   'bg-muted/15 text-muted',
  };

  return (
    <Modal isOpen onClose={onClose} title={session.title} className="max-w-lg">
      <div className="space-y-5">
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
            </svg>
            <span className="text-text">{fmtDate(session.scheduledAt)} at {fmtTime(session.scheduledAt)}</span>
          </div>
          <div className="flex items-center gap-2 text-muted">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-text">{session.duration} minutes</span>
          </div>
          <div className="flex items-center gap-2 text-muted">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
            <span className="text-text">{mentorName}</span>
          </div>
          {session.meetLink && (
            <div className="flex items-center gap-2 text-muted">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
              </svg>
              <a href={session.meetLink} target="_blank" rel="noopener noreferrer"
                className="text-chart hover:underline truncate">{session.meetLink}</a>
            </div>
          )}
        </div>

        {session.agenda && (
          <div>
            <p className="text-xs text-muted uppercase tracking-wider font-semibold mb-1.5">Agenda</p>
            <p className="text-sm text-text whitespace-pre-wrap">{session.agenda}</p>
          </div>
        )}

        {merged.length > 0 && (
          <div>
            <p className="text-xs text-muted uppercase tracking-wider font-semibold mb-2">
              Participants ({merged.length})
            </p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
              {merged.map((p: any, i: number) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-text">{p.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor[p.status] ?? 'bg-muted/15 text-muted'}`}>
                    {p.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Meet Link Section */}
        <div className="border-t border-line pt-4">
          {session.meetLink ? (
            <div className="space-y-2">
              <p className="text-xs text-muted uppercase tracking-wider font-semibold">Meeting Link</p>
              <a href={session.meetLink} target="_blank" rel="noopener noreferrer"
                className="text-sm text-chart hover:underline break-all">{session.meetLink}</a>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={linkInput}
                  onChange={e => setLinkInput(e.target.value)}
                  placeholder="Replace with new link…"
                  className="flex-1 bg-surface border border-line rounded-lg px-3 py-2 text-sm text-text placeholder:text-muted focus:outline-none focus:border-chart/50"
                />
                <button
                  disabled={savingLink || !linkInput.trim()}
                  onClick={async () => {
                    setSavingLink(true);
                    try {
                      await setSessionMeetLink(session._id || session.id, linkInput.trim());
                      onLinkSaved(session._id || session.id, linkInput.trim());
                      setLinkInput('');
                    } catch {}
                    setSavingLink(false);
                  }}
                  className="px-3 py-2 text-sm font-medium bg-chart text-white rounded-lg hover:bg-chart/90 disabled:opacity-50 transition-colors"
                >
                  {savingLink ? '…' : 'Update'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted uppercase tracking-wider font-semibold">Add Meeting Link</p>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={linkInput}
                  onChange={e => setLinkInput(e.target.value)}
                  placeholder="https://meet.google.com/… or https://zoom.us/…"
                  className="flex-1 bg-surface border border-line rounded-lg px-3 py-2 text-sm text-text placeholder:text-muted focus:outline-none focus:border-chart/50"
                />
                <button
                  disabled={savingLink || !linkInput.trim()}
                  onClick={async () => {
                    setSavingLink(true);
                    try {
                      await setSessionMeetLink(session._id || session.id, linkInput.trim());
                      onLinkSaved(session._id || session.id, linkInput.trim());
                      setLinkInput('');
                    } catch {}
                    setSavingLink(false);
                  }}
                  className="px-3 py-2 text-sm font-medium bg-chart text-white rounded-lg hover:bg-chart/90 disabled:opacity-50 transition-colors"
                >
                  {savingLink ? 'Saving…' : 'Save & Notify'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-1">
          {!past && session.meetLink && (
            <a href={session.meetLink} target="_blank" rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-chart text-white text-sm font-medium rounded-lg hover:bg-chart/90 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
              </svg>
              Join Session
            </a>
          )}
          <button
            onClick={() => { onClose(); onDelete(session._id || session.id); }}
            className="px-4 py-2.5 text-sm font-medium text-danger border border-danger/30 rounded-lg hover:bg-danger/10 transition-colors"
          >
            Delete Session
          </button>
        </div>
      </div>
    </Modal>
  );
};

/* ── Scheduler Page ── */
const GroupSessionScheduler: React.FC = () => {
  const [sessions, setSessions]         = useState<any[]>([]);
  const [mentors, setMentors]           = useState<MentorOption[]>([]);
  const [founders, setFounders]         = useState<FounderOption[]>([]);
  const [loading, setLoading]           = useState(true);
  const [showModal, setShowModal]       = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [detailSession, setDetail]      = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleting, setDeleting]         = useState(false);

  const [sessionTitle, setSessionTitle]                   = useState('');
  const [selectedMentor, setSelectedMentor]               = useState('');
  const [sessionDate, setSessionDate]                     = useState('');
  const [sessionTime, setSessionTime]                     = useState('');
  const [duration, setDuration]                           = useState('60');
  const [meetLink, setMeetLink]                           = useState('');
  const [agenda, setAgenda]                               = useState('');
  const [selectedParticipants, setSelectedParticipants]   = useState<string[]>([]);
  const [participantSearch, setParticipantSearch]         = useState('');
  const [participantDropOpen, setParticipantDropOpen]     = useState(false);
  const [mentorSlots, setMentorSlots]                     = useState<MentorWithSlots['slots']>([]);
  const [loadingSlots, setLoadingSlots]                   = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [sessionData, mentorData, founderData] = await Promise.all([
          sessionService.getGroupSessions(),
          getUsers('mentor'),
          sessionService.getEligibleParticipants(),
        ]);
        setMentors(mentorData.map((m: any) => ({ id: m._id, name: m.name })));
        setFounders(founderData.map((f: any) => ({
          id: f._id, name: f.name, startupName: f.founder?.startupName || f.company || '',
        })));
        setSessions(Array.isArray(sessionData) ? sessionData : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setParticipantDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!selectedMentor) { setMentorSlots([]); return; }
    setLoadingSlots(true);
    getMentorSlots(7)
      .then(all => {
        const found = all.find(m => String(m.mentor._id) === selectedMentor);
        setMentorSlots(found?.slots || []);
      })
      .catch(() => setMentorSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedMentor]);

  const pickSlot = (slot: MentorWithSlots['slots'][0]) => {
    const d = new Date(slot.scheduledAt);
    const yyyy = d.getFullYear();
    const mm   = String(d.getMonth() + 1).padStart(2, '0');
    const dd   = String(d.getDate()).padStart(2, '0');
    setSessionDate(`${yyyy}-${mm}-${dd}`);
    setSessionTime(slot.startTime);
  };

  const resetForm = () => {
    setSessionTitle(''); setSelectedMentor(''); setSessionDate(''); setSessionTime('');
    setDuration('60'); setMeetLink(''); setAgenda(''); setSelectedParticipants([]); setParticipantSearch('');
    setMentorSlots([]);
  };

  const handleCreate = async () => {
    if (!sessionTitle.trim() || !selectedMentor || !sessionDate || !sessionTime) return;
    setSubmitting(true);
    try {
      const scheduledAt = new Date(`${sessionDate}T${sessionTime}`).toISOString();
      const result = await sessionService.createGroupSession({
        title: sessionTitle.trim(), mentorId: selectedMentor,
        participants: selectedParticipants, scheduledAt,
        duration: parseInt(duration) || 60,
        meetLink: meetLink.trim() || undefined,
        agenda: agenda.trim() || undefined,
      });
      setSessions(prev => [result, ...prev]);
      setShowModal(false);
      resetForm();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await sessionService.deleteGroupSession(deleteTarget);
      setSessions(prev => prev.filter(s => (s._id || s.id) !== deleteTarget));
      setDeleteTarget(null);
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  const toggleParticipant = (id: string) =>
    setSelectedParticipants(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);

  const filteredFounders = founders.filter(f =>
    f.name.toLowerCase().includes(participantSearch.toLowerCase()) ||
    f.startupName.toLowerCase().includes(participantSearch.toLowerCase())
  );
  const selectedFounderNames = founders.filter(f => selectedParticipants.includes(f.id)).map(f => f.name);
  const canSubmit = sessionTitle.trim() && selectedMentor && sessionDate && sessionTime;

  return (
    <div className="section-gap">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-text">Group Sessions</h1>
          <p className="text-muted">Schedule and manage group mentor sessions</p>
        </div>
        <Button variant="primary" onClick={() => { resetForm(); setShowModal(true); }}>
          New Session
        </Button>
      </div>

      {/* Sessions Grid */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
        </div>
      ) : sessions.length === 0 ? (
        <EmptyState title="No sessions scheduled" description="Create a group mentor session to get started." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sessions.map(session => {
            const mentorName = session.mentorId?.name || 'TBA';
            const participantCount = session.participants?.length ?? session.attendees?.length ?? 0;
            const past = new Date(session.scheduledAt).getTime() + (session.duration || 60) * 60 * 1000 < Date.now();
            const id = session._id || session.id;

            return (
              <div key={id} className="card flex flex-col gap-4">
                {/* Top */}
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-text font-semibold text-sm leading-snug flex-1">{session.title}</h3>
                  <span className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_BADGE[session.status] ?? 'bg-muted/15 text-muted'}`}>
                    {session.status?.replace('_', ' ')}
                  </span>
                </div>

                {/* Meta */}
                <div className="space-y-1.5 text-sm text-muted">
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                    </svg>
                    {fmtDate(session.scheduledAt)}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {fmtTime(session.scheduledAt)} · {session.duration} min
                  </div>
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    </svg>
                    {mentorName} · {participantCount} participant{participantCount !== 1 ? 's' : ''}
                  </div>
                </div>

                {session.agenda && (
                  <p className="text-xs text-muted border-t border-line pt-3 line-clamp-2">{session.agenda}</p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 border-t border-line pt-3 mt-auto">
                  {!past && session.meetLink ? (
                    <a href={session.meetLink} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-chart text-white text-xs font-medium rounded-lg hover:bg-chart/90 transition-colors">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                      </svg>
                      Join
                    </a>
                  ) : !past ? (
                    <button onClick={() => setDetail(session)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-warning/10 text-warning rounded-lg hover:bg-warning/20 transition-colors">
                      + Add Link
                    </button>
                  ) : null}
                  <button onClick={() => setDetail(session)}
                    className="px-3 py-1.5 text-xs font-medium text-muted border border-line rounded-lg hover:text-text hover:border-chart/30 transition-colors">
                    Details
                  </button>
                  <button onClick={() => setDeleteTarget(id)}
                    className="ml-auto px-3 py-1.5 text-xs font-medium text-danger border border-danger/20 rounded-lg hover:bg-danger/10 transition-colors">
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      {detailSession && (
        <DetailModal
          session={detailSession}
          onClose={() => setDetail(null)}
          onDelete={id => { setDetail(null); setDeleteTarget(id); }}
          onLinkSaved={(id, link) => {
            setSessions(prev => prev.map(s => (s._id || s.id) === id ? { ...s, meetLink: link } : s));
            setDetail(prev => prev ? { ...prev, meetLink: link } : prev);
          }}
        />
      )}

      {/* Create Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="New Group Session">
        <div className="space-y-4">
          <Input label="Session Title" value={sessionTitle} onChange={e => setSessionTitle(e.target.value)}
            placeholder="e.g. MVP Feedback Session" required maxLength={120} />

          <Select label="Assign Mentor" options={mentors.map(m => ({ value: m.id, label: m.name }))}
            value={selectedMentor} onChange={e => setSelectedMentor(e.target.value)}
            placeholder="Select a mentor" required />

          {/* Mentor Available Slots */}
          {selectedMentor && (
            <div>
              <p className="text-sm font-medium text-text mb-2">Available Slots (next 7 days)</p>
              {loadingSlots ? (
                <p className="text-muted text-xs">Loading slots…</p>
              ) : mentorSlots.length === 0 ? (
                <p className="text-muted text-xs">No availability set for this mentor.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {mentorSlots.map((slot, i) => {
                    const d = new Date(slot.scheduledAt);
                    const label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) + `, ${slot.startTime}`;
                    return (
                      <button
                        key={i}
                        type="button"
                        disabled={slot.booked}
                        onClick={() => pickSlot(slot)}
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                          slot.booked
                            ? 'border-line text-muted opacity-50 cursor-not-allowed'
                            : 'border-chart/40 text-chart hover:bg-chart/10 cursor-pointer'
                        }`}
                      >
                        {slot.booked ? `${label} (Booked)` : label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Participants */}
          <div>
            <label className="block text-sm font-medium text-text mb-1.5">
              Participants
              {selectedParticipants.length > 0 && (
                <span className="ml-2 text-xs font-normal text-chart">{selectedParticipants.length} selected</span>
              )}
            </label>
            <div ref={dropRef} className="relative">
              <button type="button" onClick={() => setParticipantDropOpen(v => !v)}
                className="w-full bg-surface border border-line rounded-card px-3.5 py-2.5 text-sm text-left flex items-center justify-between gap-2 hover:border-chart/40 transition-colors">
                <span className={selectedFounderNames.length ? 'text-text' : 'text-muted'}>
                  {selectedFounderNames.length
                    ? selectedFounderNames.slice(0, 3).join(', ') + (selectedFounderNames.length > 3 ? ` +${selectedFounderNames.length - 3}` : '')
                    : 'Select founders…'}
                </span>
                <svg className="w-4 h-4 text-muted flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {participantDropOpen && (
                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-shell border border-line rounded-card shadow-lg max-h-56 flex flex-col">
                  <div className="p-2 border-b border-line">
                    <input type="text" value={participantSearch} onChange={e => setParticipantSearch(e.target.value)}
                      placeholder="Search founders…" autoFocus
                      className="w-full bg-surface border border-line rounded-lg px-3 py-1.5 text-sm text-text placeholder:text-muted" />
                  </div>
                  <div className="overflow-y-auto flex-1">
                    {filteredFounders.length === 0 ? (
                      <p className="text-muted text-sm p-3 text-center">No founders found</p>
                    ) : filteredFounders.map(f => (
                      <label key={f.id} className="flex items-center gap-3 px-3 py-2 hover:bg-surface cursor-pointer">
                        <input type="checkbox" checked={selectedParticipants.includes(f.id)}
                          onChange={() => toggleParticipant(f.id)} className="accent-chart w-4 h-4 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm text-text font-medium truncate">{f.name}</div>
                          {f.startupName && <div className="text-xs text-muted truncate">{f.startupName}</div>}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input label="Date" type="date" value={sessionDate} onChange={e => setSessionDate(e.target.value)} required />
            <Input label="Time" type="time" value={sessionTime} onChange={e => setSessionTime(e.target.value)} required />
          </div>

          <Select label="Duration" options={DURATION_OPTIONS} value={duration} onChange={e => setDuration(e.target.value)} />

          <Input label="Meeting Link (optional)" type="url" value={meetLink} onChange={e => setMeetLink(e.target.value)}
            placeholder="https://meet.google.com/…" />

          <Textarea label="Agenda (optional)" placeholder="Describe the session agenda…" value={agenda}
            onChange={e => setAgenda(e.target.value)} rows={3} />

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</Button>
            <Button variant="primary" onClick={handleCreate} disabled={submitting || !canSubmit}>
              {submitting ? 'Scheduling…' : 'Schedule Session'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Session">
        <p className="text-sm text-muted mb-6">Delete this session? This cannot be undone.</p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="primary" onClick={handleDelete} disabled={deleting} className="!bg-danger hover:!bg-danger/90">
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default GroupSessionScheduler;
